/* My third brain: the find-or-ask box, the status slider, the graph and the reader overlay.
   Loaded only by brain.hbs, custom-brain-topic.hbs and custom-brain-links.hbs.
   Every template works without this file; it only enhances. */
(function () {
    'use strict';

    const STALE_DAYS = 60;
    const ORDER = ['using', 'trialling', 'watching', 'dropped'];
    const LABEL = {using: 'Using', trialling: 'Trialling', watching: 'Watching', dropped: 'Dropped'};
    const SIDE_SECTIONS = ['links', 'on the blog', 'history'];

    const $ = (selector, root) => (root || document).querySelector(selector);
    const $$ = (selector, root) => Array.from((root || document).querySelectorAll(selector));
    const esc = value => String(value == null ? '' : value).replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;'}[c]));
    const isTyping = el => el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

    // ---------- matching ----------

    // The same matcher as the header's find-or-ask dialog (lib/find.js, in main.min.js)
    const matcher = window.FlorezFind.matcher;

    // ---------- dates ----------

    function toDay(value) {
        const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
        const date = match ? new Date(+match[1], match[2] - 1, +match[3]) : new Date(value);
        date.setHours(0, 0, 0, 0);
        return date;
    }

    function daysAgo(value) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return Math.round((today - toDay(value)) / 86400000);
    }

    const relative = (value) => {
        const days = daysAgo(value);
        return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
    };
    const isStale = (value, status) => status !== 'dropped' && daysAgo(value) > STALE_DAYS;
    const statusOf = (slug) => {
        const status = (slug || '').replace('hash-status-', '');
        return ORDER.includes(status) ? status : 'watching';
    };

    function glyph(status, size) {
        const s = size || 14;
        const shapes = {
            using: '<circle cx="7" cy="7" r="5.5" fill="currentColor"/>',
            trialling: '<circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" stroke-width="2"/><path d="M7 1.5A5.5 5.5 0 0 0 7 12.5Z" fill="currentColor"/>',
            watching: '<circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" stroke-width="2"/>',
            dropped: '<circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" stroke-width="2" opacity=".6"/><path d="M3.15 10.85L10.85 3.15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>'
        };
        return `<svg class="brain-glyph" width="${s}" height="${s}" viewBox="0 0 14 14" aria-hidden="true" focusable="false">${shapes[status]}</svg>`;
    }

    const EXT_ICON = '<svg class="brain-ext" width="11" height="11" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M5 2H2v8h8V7M7 1h4v4M11 1L5.5 6.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    function hostOf(url) {
        try {
            return new URL(url, location.href).hostname.replace(/^www\./, '');
        } catch (e) {
            return '';
        }
    }

    function isExternal(a) {
        return /^https?:$/.test(a.protocol) && a.hostname !== location.hostname;
    }

    /** Text of an <li> after its first link, without the leading separator ("— desc", "· date"). */
    function trailingText(li, link) {
        let text = '';
        for (let node = link.nextSibling; node; node = node.nextSibling) {
            text += node.textContent;
        }
        return text.replace(/^\s*[—–·:-]\s*/, '').trim();
    }

    /** Parse a links page body: an H2 per topic, then a list of "<a>Title</a> — description". */
    function parseLinks(root) {
        const links = [];
        let group = '';
        Array.from(root.children).forEach((el) => {
            if (el.tagName === 'H2') {
                group = el.textContent.trim();
            } else if (el.tagName === 'UL' || el.tagName === 'OL') {
                $$(':scope > li', el).forEach((li) => {
                    const a = $('a[href]', li);
                    if (!a) {
                        return;
                    }
                    const description = trailingText(li, a);
                    links.push({
                        group,
                        title: a.textContent.trim(),
                        url: a.href,
                        description,
                        text: [a.textContent, description, a.href, group].join(' ').toLowerCase()
                    });
                });
            }
        });
        return links;
    }

    // ---------- documents (topic + links pages, standalone and in the reader) ----------

    function markExternalLinks(root) {
        $$('.brain-notes a[href]', root).forEach((a) => {
            if (!isExternal(a) || a.dataset.brainExt) {
                return;
            }
            a.dataset.brainExt = '1';
            a.target = '_blank';
            a.rel = 'noopener';
            a.insertAdjacentHTML('beforeend', `${EXT_ICON}<span class="brain-sr"> (opens in a new tab)</span>`);
        });
    }

    function updateReviewed(doc) {
        const el = $('.brain-reviewed[data-reviewed]', doc);
        if (!el) {
            return;
        }
        const time = $('time', el);
        const date = time ? time.textContent : el.dataset.reviewed;
        const stale = isStale(el.dataset.reviewed, statusOf(doc.dataset.status));
        el.classList.toggle('is-stale', stale);
        el.textContent = `${stale ? 'Due for review. Last reviewed' : 'Last reviewed'} ${date} (${relative(el.dataset.reviewed)})`;
    }

    /** Move the "Links", "On the blog" and "History" sections of a topic body into the sidebar. */
    function moveSideSections(doc) {
        const notes = $('.brain-notes', doc);
        const side = $('[data-doc-side]', doc);
        if (!notes || !side || side.dataset.moved) {
            return;
        }
        side.dataset.moved = '1';

        const moved = [];
        $$(':scope > h2', notes).forEach((heading) => {
            const name = heading.textContent.trim().toLowerCase();
            if (!SIDE_SECTIONS.includes(name)) {
                return;
            }
            const section = document.createElement('section');
            section.className = 'brain-side-section brain-notes-moved';
            section.dataset.section = name;
            let node = heading.nextElementSibling;
            section.appendChild(heading);
            while (node && node.tagName !== 'H2') {
                const next = node.nextElementSibling;
                section.appendChild(node);
                node = next;
            }
            moved.push(section);
        });

        moved.sort((a, b) => SIDE_SECTIONS.indexOf(a.dataset.section) - SIDE_SECTIONS.indexOf(b.dataset.section));
        moved.reverse().forEach(section => side.prepend(section));

        moved.forEach((section) => {
            const list = $('ul, ol', section);
            if (!list) {
                return;
            }
            if (section.dataset.section === 'history') {
                list.classList.add('brain-history');
                return;
            }
            $$(':scope > li', list).forEach((li) => {
                const a = $('a[href]', li);
                const extra = a ? trailingText(li, a) : '';
                if (!a) {
                    return;
                }
                // Links: title + domain (the description lives on the links page).
                // On the blog: title + date.
                const meta = section.dataset.section === 'links' ? hostOf(a.href) : extra;
                if (section.dataset.section === 'links' && extra) {
                    a.title = extra;
                }
                while (a.nextSibling) {
                    a.nextSibling.remove();
                }
                if (meta) {
                    li.insertAdjacentHTML('beforeend', `<span class="brain-dom">${esc(meta)}</span>`);
                }
            });
            // Posts without a link keep their "Title · date" text, split into two lines
            $$(':scope > li', list).forEach((li) => {
                if ($('a', li) || !li.textContent.includes('·')) {
                    return;
                }
                const [title, ...date] = li.textContent.split('·');
                li.innerHTML = `${esc(title.trim())}<span class="brain-dom">${esc(date.join('·').trim())}</span>`;
            });
        });
    }

    function enhanceLinksDoc(doc, initialFilter) {
        const body = $('[data-links-body]', doc);
        if (!body || body.dataset.enhanced) {
            return;
        }
        body.dataset.enhanced = '1';

        const topics = {};
        $$('[data-links-topics] li[data-title]', doc).forEach((li) => {
            const a = $('a', li);
            topics[li.dataset.title.toLowerCase()] = a && a.href;
        });

        let total = 0;
        let group = '';
        Array.from(body.children).forEach((el) => {
            if (el.tagName === 'H2') {
                group = el.textContent.trim();
                const url = topics[group.toLowerCase()];
                if (url) {
                    el.insertAdjacentHTML('beforeend', ` <a class="brain-lp-open" href="${esc(url)}" data-brain-open>Open topic</a>`);
                }
                return;
            }
            if (el.tagName !== 'UL' && el.tagName !== 'OL') {
                return;
            }
            $$(':scope > li', el).forEach((li) => {
                const a = $('a[href]', li);
                if (!a) {
                    return;
                }
                total++;
                const description = trailingText(li, a);
                while (a.nextSibling) {
                    a.nextSibling.remove();
                }
                li.insertAdjacentHTML('beforeend', `<span class="brain-dom">${esc(hostOf(a.href))}</span>${description ? `<span class="brain-lp-desc">${esc(description)}</span>` : ''}`);
                li.dataset.q = [a.textContent, description, a.href, group].join(' ').toLowerCase();
            });
        });
        markExternalLinks(doc);

        const input = $('[data-links-filter]', doc);
        const none = $('[data-links-none]', doc);
        if (!input) {
            return;
        }
        input.placeholder = `Filter ${total} link${total === 1 ? '' : 's'}`;

        const apply = () => {
            const match = matcher(input.value);
            let any = false;
            let heading = null;
            let headingHits = 0;
            const closeGroup = () => {
                if (heading) {
                    heading.hidden = headingHits === 0;
                }
            };
            Array.from(body.children).forEach((el) => {
                if (el.tagName === 'H2') {
                    closeGroup();
                    heading = el;
                    headingHits = 0;
                    return;
                }
                const items = $$(':scope > li[data-q]', el);
                if (!items.length) {
                    return;
                }
                let hits = 0;
                items.forEach((li) => {
                    const ok = match.test(li.dataset.q);
                    li.hidden = !ok;
                    hits += ok ? 1 : 0;
                });
                el.hidden = hits === 0;
                headingHits += hits;
                any = any || hits > 0;
            });
            closeGroup();
            if (none) {
                none.hidden = any || !match.active;
            }
        };
        input.addEventListener('input', apply);
        if (initialFilter) {
            input.value = initialFilter;
        }
        apply();
    }

    function enhanceDoc(doc, options) {
        if (!doc) {
            return;
        }
        if (doc.dataset.brainDoc === 'links') {
            enhanceLinksDoc(doc, options && options.filter);
            return;
        }
        updateReviewed(doc);
        markExternalLinks(doc); // before the move: it only looks inside .brain-notes
        moveSideSections(doc);
        $$('[data-side-topics] li[data-slug]', doc).forEach((li) => {
            if (li.dataset.slug === doc.dataset.slug) {
                const a = $('a', li);
                a.setAttribute('aria-current', 'page');
            }
        });
    }

    // Standalone topic / links page
    const standalone = $('main [data-brain-doc]');
    if (standalone && !$('[data-brain-tiles]')) {
        enhanceDoc(standalone);
        // Command boxes, image zoom and the outline (assets/js/lib/notes.js), after the sidebar move
        if (window.FlorezNotes) {
            window.FlorezNotes.enhance(standalone);
        }
        return;
    }

    // ---------- /brain/ overview ----------

    const tilesEl = $('[data-brain-tiles]');
    if (!tilesEl) {
        return;
    }

    const search = $('[data-brain-search]');
    const announce = $('[data-brain-announce]');
    const empty = $('[data-brain-empty]');
    const toggleAll = $('[data-toggle-all]');
    const reader = $('[data-brain-reader]');
    const graphView = $('[data-brain-graph]');
    let filter = 'all';
    let view = 'tiles';
    let indexUrl = location.href; // where closing the reader returns to

    const TILES = $$('.brain-tile[data-tile]', tilesEl).map((el) => {
        const topicsJson = $('script[data-brain-topics]', el);
        const linksJson = $('script[data-brain-links]', el);
        const tile = {
            el,
            slug: el.dataset.tile,
            name: el.dataset.tileName,
            details: $('[data-tile-details]', el),
            countEl: $('[data-links-count]', el),
            linksPage: linksJson ? JSON.parse(linksJson.textContent) : null,
            topics: []
        };

        const parsed = topicsJson ? JSON.parse(topicsJson.textContent) : [];
        tile.links = tile.linksPage ? parseLinks(new DOMParser().parseFromString(tile.linksPage.html || '', 'text/html').body) : [];
        tile.topics = parsed.map(({tags, page}) => {
            const status = statusOf(tags.find(tag => tag.startsWith('hash-status-')));
            const own = tile.links.filter(link => link.group.toLowerCase() === page.title.toLowerCase());
            return {
                tile,
                status,
                slug: page.slug,
                url: page.url,
                title: page.title,
                reviewed: page.reviewed,
                html: page.html, // read once by the graph for its lines, then dropped
                el: $(`.brain-topic[data-slug="${CSS.escape(page.slug)}"]`, el),
                search: [page.title, page.take, page.text].concat(own.map(link => link.text)).join(' ').toLowerCase()
            };
        }).sort((a, b) => ORDER.indexOf(a.status) - ORDER.indexOf(b.status) || a.title.localeCompare(b.title));
        return tile;
    });
    const ALL_TOPICS = TILES.reduce((list, tile) => list.concat(tile.topics), []);

    // Sort topics by status, fill in tile meta and relative dates
    TILES.forEach((tile) => {
        const list = $('.brain-topics', tile.el);
        const preview = $('.brain-preview', tile.el);
        if (list) {
            tile.topics.forEach(topic => topic.el && list.appendChild(topic.el));
        }
        if (preview) {
            preview.innerHTML = tile.topics.map(topic => `<span>${esc(topic.title)}</span>`).join('');
        }

        tile.topics.forEach((topic) => {
            const age = topic.el && $('.brain-topic-age', topic.el);
            if (!age) {
                return;
            }
            const stale = isStale(topic.reviewed, topic.status);
            age.classList.toggle('is-stale', stale);
            age.title = `Last reviewed ${$('time', age) ? $('time', age).textContent : ''}`;
            age.textContent = stale ? 'Due for review' : `Reviewed ${relative(topic.reviewed)}`;
        });

        const meta = $('[data-tile-meta]', tile.el);
        if (meta && tile.topics.length) {
            const counts = ORDER.map(status => [status, tile.topics.filter(topic => topic.status === status).length]).filter(([, n]) => n);
            const newest = tile.topics.map(topic => topic.reviewed).sort((a, b) => toDay(b) - toDay(a))[0];
            const stale = tile.topics.filter(topic => isStale(topic.reviewed, topic.status)).length;
            meta.innerHTML = `<span class="brain-tile-counts">${counts.map(([status, n]) => `<span class="brain-tile-count" title="${LABEL[status]}">${glyph(status, 12)}${n}<span class="brain-sr"> ${LABEL[status]}</span></span>`).join('')}</span>` +
                `<span>Updated ${relative(newest)}</span>` +
                (stale ? `<span class="brain-stale-flag">${stale} due for review</span>` : '');
        }

        if (tile.countEl) {
            tile.countEl.dataset.total = tile.links.length;
            tile.countEl.textContent = `${tile.links.length} external link${tile.links.length === 1 ? '' : 's'}`;
        }
    });

    // ---------- expand / collapse ----------

    const details = () => TILES.map(tile => tile.details).filter(Boolean);
    function syncToggleAll() {
        const open = details().length && details().every(d => d.open);
        toggleAll.textContent = open ? 'Collapse all' : 'Expand all';
    }
    toggleAll.addEventListener('click', () => {
        const open = toggleAll.textContent === 'Expand all';
        details().forEach((d) => {
            d.open = open;
        });
        syncToggleAll();
    });
    details().forEach(d => d.addEventListener('toggle', syncToggleAll));

    // Tiles open in place. A closed tile next to an open one keeps its own height
    // instead of stretching to the open tile's height.
    function syncRows() {
        const visible = TILES.filter(tile => !tile.el.hidden);
        const isOpen = tile => Boolean(tile.details && tile.details.open);
        const openRows = new Set(visible.filter(isOpen).map(tile => tile.el.offsetTop));
        TILES.forEach((tile) => {
            tile.el.classList.toggle('is-beside-open', !isOpen(tile) && openRows.has(tile.el.offsetTop));
        });
    }
    details().forEach(d => d.addEventListener('toggle', syncRows));
    window.addEventListener('resize', syncRows);

    function openTileFromHash() {
        const tile = TILES.find(t => `#${t.slug}` === location.hash);
        if (tile && tile.details) {
            tile.details.open = true;
            syncToggleAll();
        }
    }
    window.addEventListener('hashchange', openTileFromHash);
    openTileFromHash();

    // ---------- graph ----------
    // Topics, use cases and articles as dots, links between them as lines, tiles as clusters. The
    // lines come from the links in each topic's body: [[wikilinks]] to other topics and "On the blog"
    // posts. Use cases always show; articles only when a topic links to them.

    const graph = graphView ? createGraph() : null;

    function createGraph() {
        const svg = $('[data-graph-svg]', graphView);
        const viewport = $('[data-graph-viewport]', svg);
        const edgesEl = $('[data-graph-edges]', svg);
        const nodesEl = $('[data-graph-nodes]', svg);
        const card = $('[data-graph-card]', graphView);
        const graphTip = $('[data-graph-tip]', graphView);
        const panel = $('[data-graph-panel]', graphView);
        const panelBody = $('[data-graph-body]', panel);
        const crumbEl = $('[data-graph-crumb]', panel);
        const statsEl = $('[data-graph-stats]', graphView);
        const postsJson = $('script[data-brain-posts]');
        const POSTS = postsJson ? JSON.parse(postsJson.textContent) : [];
        const ucJson = $('script[data-brain-usecases]');
        const USE_CASES = new Set(ucJson ? JSON.parse(ucJson.textContent) : []);
        const reduceMotion = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
        const hosts = new Set([location.hostname, hostOf(graphView.dataset.siteUrl || '')]);
        const opts = {tiles: true, usecases: true, articles: true};
        const pos = new Map(); // node id -> position, kept when options change
        const camera = {x: 0, y: 0, k: 1};
        let nodes = [];
        let edges = [];
        let byId = new Map();
        let adjacency = new Map();
        let links = null;
        let hoverId = null;
        let selectedId = null;
        let match = matcher('');
        let alpha = 0;
        let frame = 0;

        const pathOf = (url) => {
            try {
                const u = new URL(url, location.href);
                return hosts.has(u.hostname.replace(/^www\./, '')) || hosts.has(u.hostname) ? u.pathname.replace(/\/?$/, '/').toLowerCase() : '';
            } catch (e) {
                return '';
            }
        };
        const short = (text, n) => (text.length > n ? `${text.slice(0, n - 1)}…` : text);
        /** Cut at a word, so a use case reads "Find out if the EU AI Act…", not "…app…". */
        const shortWords = (text, n) => {
            if (text.length <= n) {
                return text;
            }
            const cut = text.slice(0, n).replace(/[\s,:;(]+\S*$/, '');
            return `${cut || text.slice(0, n - 1)}…`;
        };
        const isUseCase = node => node.kind === 'post' && node.uc;
        const postKind = node => (isUseCase(node) ? 'uc' : 'article');
        const colorOf = node => `is-c${node.tileIndex % 8}`;

        /** Lines between notes, read once from the topics' html. */
        function readLinks() {
            const byPath = new Map();
            ALL_TOPICS.forEach((topic) => {
                byPath.set(pathOf(topic.url), `topic:${topic.slug}`);
                byPath.set(`/${topic.slug}/`, `topic:${topic.slug}`); // wikilinks point at /<slug>/
            });
            POSTS.forEach((post) => {
                byPath.set(pathOf(post.url), `post:${post.slug}`);
                byPath.set(`/${post.slug}/`, `post:${post.slug}`);
            });
            byPath.delete('');
            const seen = new Set();
            const found = [];
            ALL_TOPICS.forEach((topic) => {
                const from = `topic:${topic.slug}`;
                const doc = new DOMParser().parseFromString(topic.html || '', 'text/html');
                $$('a[href]', doc).forEach((a) => {
                    const to = byPath.get(pathOf(a.getAttribute('href')));
                    const key = [from, to].sort().join(' ');
                    if (!to || to === from || seen.has(key)) {
                        return;
                    }
                    seen.add(key);
                    found.push({a: from, b: to, kind: to.startsWith('post:') ? 'blog' : 'link'});
                });
                delete topic.html;
            });
            return found;
        }

        function build() {
            if (!links) {
                links = readLinks();
            }
            nodes = [];
            if (opts.tiles) {
                TILES.forEach((tile, i) => nodes.push({id: `tile:${tile.slug}`, kind: 'tile', name: tile.name, tile, tileIndex: i}));
            }
            TILES.forEach((tile, i) => tile.topics.forEach(topic => nodes.push({
                id: `topic:${topic.slug}`, kind: 'topic', name: topic.title, topic, tile, tileIndex: i, status: topic.status, url: topic.url
            })));
            byId = new Map(nodes.map(node => [node.id, node]));
            const linked = new Set(links.filter(l => l.kind === 'blog').map(l => l.b));
            POSTS.forEach((post) => {
                const uc = USE_CASES.has(post.slug);
                // Use cases are what the site offers, so they always show. Articles only when a topic
                // links to them; the graph is about how notes connect.
                if (uc ? !opts.usecases : !opts.articles || !linked.has(`post:${post.slug}`)) {
                    return;
                }
                const node = {id: `post:${post.slug}`, kind: 'post', uc, name: post.title, post, url: post.url};
                nodes.push(node);
                byId.set(node.id, node);
            });

            edges = [];
            if (opts.tiles) {
                nodes.filter(node => node.kind === 'topic').forEach(node => edges.push({a: `tile:${node.tile.slug}`, b: node.id, kind: 'structure'}));
            }
            links.forEach(link => byId.has(link.a) && byId.has(link.b) && edges.push(Object.assign({}, link)));

            adjacency = new Map(nodes.map(node => [node.id, []]));
            edges.forEach((edge) => {
                adjacency.get(edge.a).push({id: edge.b, kind: edge.kind});
                adjacency.get(edge.b).push({id: edge.a, kind: edge.kind});
            });

            const n = TILES.length || 1;
            // Tiles sit on an ellipse shaped like the card: wide on desktop, tall on a phone
            const ratio = Math.min(2, Math.max(0.5, (svg.clientWidth || 900) / (svg.clientHeight || 680)));
            const rx = 330 * Math.sqrt(ratio);
            const ry = 330 / Math.sqrt(ratio);
            nodes.forEach((node) => {
                const degree = adjacency.get(node.id).filter(x => x.kind !== 'structure').length;
                node.r = isUseCase(node) ? 9 : node.kind === 'post' ? 6 : 8 + Math.min(degree, 5) * 1.3;
                const angle = (node.tileIndex === undefined ? Math.random() : node.tileIndex / n) * Math.PI * 2;
                node.anchor = node.kind === 'tile' ? {x: Math.cos(angle) * rx, y: Math.sin(angle) * ry} : null;
                if (!pos.has(node.id)) {
                    const spread = node.kind === 'tile' ? 0.9 : node.kind === 'post' ? 0.2 : 1.05 + Math.random() * 0.2;
                    pos.set(node.id, {x: Math.cos(angle) * rx * spread + (Math.random() - 0.5) * 40, y: Math.sin(angle) * ry * spread + (Math.random() - 0.5) * 40, vx: 0, vy: 0});
                }
                node.p = pos.get(node.id);
            });
            if (selectedId && !byId.has(selectedId)) {
                select(null);
            }

            draw();
            const count = (n, one, many) => `${n} ${n === 1 ? one : many}`;
            statsEl.textContent = [
                count(ALL_TOPICS.length, 'topic', 'topics'),
                count(nodes.filter(isUseCase).length, 'use case', 'use cases'),
                count(nodes.filter(x => x.kind === 'post' && !x.uc).length, 'article', 'articles'),
                count(edges.filter(e => e.kind !== 'structure').length, 'link', 'links')
            ].join(' · ');
        }

        // ----- drawing -----

        function dot(status, r) {
            const ring = `<circle class="g-ring" r="${r}"${status === 'dropped' ? ' stroke-dasharray="3 2.5"' : ''}/>`;
            if (status === 'using') {
                return `<circle class="g-fill" r="${r}"/>`;
            }
            if (status === 'trialling') {
                return `${ring}<path class="g-fill" d="M0 ${-r}A${r} ${r} 0 0 0 0 ${r}Z"/>`;
            }
            if (status === 'dropped') {
                const d = (r * 0.7).toFixed(1);
                return `${ring}<path class="g-slash" d="M${-d} ${d}L${d} ${-d}"/>`;
            }
            return ring;
        }

        function draw() {
            edgesEl.innerHTML = edges.map(edge => `<line class="edge is-${edge.kind}"/>`).join('');
            nodesEl.innerHTML = nodes.map((node) => {
                let shape;
                let label;
                let aria;
                if (node.kind === 'tile') {
                    const w = Math.max(90, node.name.length * 8.2 + 26);
                    shape = `<rect class="g-halo g-pill" x="${(-w / 2).toFixed(1)}" y="-15" width="${w.toFixed(1)}" height="30" rx="15"/>`;
                    label = `<text class="g-label" text-anchor="middle" dy="5">${esc(node.name)}</text>`;
                    aria = `Tile: ${node.name}`;
                } else if (isUseCase(node)) {
                    const r = node.r;
                    shape = `<rect class="g-halo" x="${-r - 4}" y="${-r - 4}" width="${2 * r + 8}" height="${2 * r + 8}" rx="6"/><rect class="g-uc" x="${-r}" y="${-r}" width="${2 * r}" height="${2 * r}" rx="4"/>`;
                    label = `<text class="g-label" text-anchor="middle" dy="${r + 16}">${esc(shortWords(node.name, 30))}</text>`;
                    aria = `Use case: ${node.name}`;
                } else if (node.kind === 'post') {
                    const r = node.r + 1;
                    shape = `<circle class="g-halo" r="${r + 3}"/><path class="g-post" d="M0 ${-r}L${r} 0L0 ${r}L${-r} 0Z"/>`;
                    label = `<text class="g-label" text-anchor="middle" dy="${r + 15}">${esc(shortWords(node.name, 30))}</text>`;
                    aria = `Article: ${node.name}`;
                } else {
                    shape = `<circle class="g-halo" r="${node.r + 4}"/>${dot(node.status, node.r)}`;
                    label = `<text class="g-label" text-anchor="middle" dy="${node.r + 16}">${esc(node.name)}</text>`;
                    aria = `${node.name}, ${LABEL[node.status]}, ${node.tile.name}`;
                }
                const color = node.kind === 'post' ? ` is-${postKind(node)}` : ` ${colorOf(node)}`;
                return `<g class="node is-${node.kind}${color}" data-id="${esc(node.id)}" tabindex="0" role="button" aria-label="${esc(aria)}">${shape}${label}</g>`;
            }).join('');
            nodes.forEach((node) => {
                node.el = nodesEl.querySelector(`[data-id="${CSS.escape(node.id)}"]`);
            });
            edges.forEach((edge, i) => {
                edge.el = edgesEl.children[i];
                edge.na = byId.get(edge.a);
                edge.nb = byId.get(edge.b);
            });
            place();
            highlight();
        }

        function place() {
            nodes.forEach(node => node.el.setAttribute('transform', `translate(${node.p.x.toFixed(1)},${node.p.y.toFixed(1)})`));
            edges.forEach((edge) => {
                edge.el.setAttribute('x1', edge.na.p.x.toFixed(1));
                edge.el.setAttribute('y1', edge.na.p.y.toFixed(1));
                edge.el.setAttribute('x2', edge.nb.p.x.toFixed(1));
                edge.el.setAttribute('y2', edge.nb.p.y.toFixed(1));
            });
        }

        // ----- layout: a small force simulation, enough for a few hundred notes -----

        function step() {
            for (let i = 0; i < nodes.length; i++) {
                const na = nodes[i];
                const a = na.p;
                for (let j = i + 1; j < nodes.length; j++) {
                    const nb = nodes[j];
                    const b = nb.p;
                    let dx = b.x - a.x;
                    let dy = b.y - a.y;
                    let d2 = dx * dx + dy * dy;
                    if (d2 < 1) {
                        dx = Math.random() - 0.5;
                        dy = Math.random() - 0.5;
                        d2 = 1;
                    }
                    const d = Math.sqrt(d2);
                    const mass = (na.kind === 'tile' ? 3 : 1) * (nb.kind === 'tile' ? 3 : 1);
                    const min = (na.kind === 'tile' ? 70 : 34) + (nb.kind === 'tile' ? 70 : 34); // room for labels
                    let f = Math.min(5200 * mass / d2, 30) * alpha;
                    if (d < min) {
                        f += (min - d) * 0.25;
                    }
                    const fx = dx / d * f;
                    const fy = dy / d * f;
                    a.vx -= fx;
                    a.vy -= fy;
                    b.vx += fx;
                    b.vy += fy;
                }
            }
            edges.forEach((edge) => {
                const a = edge.na.p;
                const b = edge.nb.p;
                const length = edge.kind === 'structure' ? 95 : edge.kind === 'blog' ? 110 : 150;
                const k = edge.kind === 'structure' ? 0.06 : 0.03;
                const dx = b.x - a.x;
                const dy = b.y - a.y;
                const d = Math.sqrt(dx * dx + dy * dy) || 1;
                const f = (d - length) * k * alpha;
                a.vx += dx / d * f;
                a.vy += dy / d * f;
                b.vx -= dx / d * f;
                b.vy -= dy / d * f;
            });
            nodes.forEach((node) => {
                const p = node.p;
                if (p.fixed) {
                    p.vx = 0;
                    p.vy = 0;
                    return;
                }
                if (node.anchor) {
                    p.vx += (node.anchor.x - p.x) * 0.05 * alpha;
                    p.vy += (node.anchor.y - p.y) * 0.05 * alpha;
                }
                p.vx -= p.x * 0.004 * alpha;
                p.vy -= p.y * 0.004 * alpha;
                p.vx *= 0.55;
                p.vy *= 0.55;
                p.x += p.vx;
                p.y += p.vy;
            });
            alpha *= 0.985;
        }

        function settle(steps) {
            for (let i = 0; i < steps && alpha > 0.01; i++) {
                step();
            }
            place();
        }

        function run(heat) {
            alpha = Math.max(alpha, heat);
            if (reduceMotion) {
                settle(300);
                return;
            }
            if (frame) {
                return;
            }
            const tick = () => {
                step();
                place();
                frame = alpha > 0.01 ? requestAnimationFrame(tick) : 0;
            };
            frame = requestAnimationFrame(tick);
        }

        // ----- camera: pan, zoom, fit -----

        function applyCamera() {
            viewport.setAttribute('transform', `translate(${camera.x.toFixed(1)},${camera.y.toFixed(1)}) scale(${camera.k.toFixed(3)})`);
        }

        function moveCamera(target, ms) {
            if (!ms || reduceMotion) {
                Object.assign(camera, target);
                applyCamera();
                return;
            }
            const from = Object.assign({}, camera);
            const t0 = performance.now();
            const tick = (t) => {
                const u = Math.min(1, (t - t0) / ms);
                const e = 1 - Math.pow(1 - u, 3);
                camera.x = from.x + (target.x - from.x) * e;
                camera.y = from.y + (target.y - from.y) * e;
                camera.k = from.k + (target.k - from.k) * e;
                applyCamera();
                if (u < 1) {
                    requestAnimationFrame(tick);
                }
            };
            requestAnimationFrame(tick);
        }

        function fit(ms) {
            const w = svg.clientWidth;
            const h = svg.clientHeight;
            if (!w || !h || !nodes.length) {
                return;
            }
            let x0 = Infinity;
            let y0 = Infinity;
            let x1 = -Infinity;
            let y1 = -Infinity;
            nodes.forEach((node) => {
                x0 = Math.min(x0, node.p.x);
                y0 = Math.min(y0, node.p.y);
                x1 = Math.max(x1, node.p.x);
                y1 = Math.max(y1, node.p.y);
            });
            const pad = 80;
            const k = Math.min(1.6, w / (x1 - x0 + pad * 2), h / (y1 - y0 + pad * 2));
            moveCamera({k, x: w / 2 - (x0 + x1) / 2 * k, y: h / 2 - (y0 + y1) / 2 * k}, ms);
        }

        function zoomAt(factor, cx, cy) {
            const k = Math.max(0.3, Math.min(3, camera.k * factor));
            const f = k / camera.k;
            camera.x = cx - (cx - camera.x) * f;
            camera.y = cy - (cy - camera.y) * f;
            camera.k = k;
            applyCamera();
        }

        function centerOn(node) {
            const side = !panel.hidden && window.innerWidth > 900 ? panel.offsetWidth + 24 : 0;
            const w = svg.clientWidth - side;
            const h = svg.clientHeight - (!panel.hidden && window.innerWidth <= 900 ? panel.offsetHeight : 0);
            const k = Math.max(camera.k, 1.1);
            moveCamera({k, x: w / 2 - node.p.x * k, y: h / 2 - node.p.y * k}, 400);
        }

        // Pinch or Ctrl/Cmd + scroll zooms; a plain scroll keeps scrolling the page
        svg.addEventListener('wheel', (event) => {
            if (!event.ctrlKey && !event.metaKey) {
                return;
            }
            event.preventDefault();
            const box = svg.getBoundingClientRect();
            zoomAt(Math.exp(-event.deltaY * 0.01), event.clientX - box.left, event.clientY - box.top);
        }, {passive: false});

        $$('[data-graph-zoom]', graphView).forEach(button => button.addEventListener('click', () => {
            const mode = button.dataset.graphZoom;
            if (mode === 'fit') {
                fit(350);
            } else {
                zoomAt(mode === 'in' ? 1.25 : 0.8, svg.clientWidth / 2, svg.clientHeight / 2);
            }
        }));

        // Mouse: drag a dot to move it, drag the background to pan, click to select.
        // Touch: one finger drags a dot or taps to select, but on the background it scrolls the page
        // (touch-action: pan-y in brain.css), so the graph never traps the scroll. Two fingers pinch to
        // zoom around the point between them and pan with it.
        const pointers = new Map(); // pointerId → {x, y} in client coordinates
        let drag = null;
        let pinch = null;

        function pinchState() {
            const [a, b] = Array.from(pointers.values());
            const box = svg.getBoundingClientRect();
            return {dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, x: (a.x + b.x) / 2 - box.left, y: (a.y + b.y) / 2 - box.top};
        }

        svg.addEventListener('pointerdown', (event) => {
            if (event.button !== 0) {
                return;
            }
            if (event.pointerType === 'touch') {
                pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
                if (pointers.size === 2) {
                    // The second finger turns the gesture into a pinch: no dot drag, no tap
                    if (drag && drag.node) {
                        drag.node.p.fixed = false;
                    }
                    drag = null;
                    pinch = pinchState();
                    svg.classList.add('is-dragging');
                    hideTip();
                    return;
                }
                if (pointers.size > 2) {
                    return;
                }
            }
            const g = event.target.closest('.node');
            drag = {node: g && byId.get(g.dataset.id), x: event.clientX, y: event.clientY, cx: camera.x, cy: camera.y, moved: false, id: event.pointerId,
                pan: event.pointerType !== 'touch'};
        });
        svg.addEventListener('pointermove', (event) => {
            if (pointers.has(event.pointerId)) {
                pointers.set(event.pointerId, {x: event.clientX, y: event.clientY});
            }
            if (pinch) {
                const next = pinchState();
                camera.x += next.x - pinch.x;
                camera.y += next.y - pinch.y;
                zoomAt(next.dist / pinch.dist, next.x, next.y);
                pinch = next;
                return;
            }
            if (!drag) {
                hoverFrom(event);
                return;
            }
            if (event.pointerId !== drag.id) {
                return;
            }
            const dx = event.clientX - drag.x;
            const dy = event.clientY - drag.y;
            if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 4) {
                drag.moved = true;
                svg.setPointerCapture(drag.id);
                svg.classList.add('is-dragging');
                hideTip();
            }
            if (!drag.moved) {
                return;
            }
            if (drag.node) {
                const box = svg.getBoundingClientRect();
                drag.node.p.x = (event.clientX - box.left - camera.x) / camera.k;
                drag.node.p.y = (event.clientY - box.top - camera.y) / camera.k;
                drag.node.p.fixed = true;
                run(0.3);
                place();
            } else if (drag.pan) {
                camera.x = drag.cx + dx;
                camera.y = drag.cy + dy;
                applyCamera();
            }
        });
        const endDrag = (event) => {
            pointers.delete(event.pointerId);
            if (pinch) {
                if (pointers.size >= 2) {
                    pinch = pinchState();
                    return;
                }
                pinch = null;
                if (pointers.size === 1) {
                    // One finger left: carry on as a pan from where it is now, without a jump
                    const [[id, p]] = Array.from(pointers.entries());
                    drag = {node: null, x: p.x, y: p.y, cx: camera.x, cy: camera.y, moved: true, id, pan: true};
                    return;
                }
                svg.classList.remove('is-dragging');
                return;
            }
            if (!drag || event.pointerId !== drag.id) {
                return;
            }
            if (drag.node) {
                drag.node.p.fixed = false;
            }
            if (!drag.moved && event.type === 'pointerup') {
                select(drag.node ? drag.node.id : null);
            }
            drag = null;
            svg.classList.remove('is-dragging');
        };
        svg.addEventListener('pointerup', endDrag);
        svg.addEventListener('pointercancel', endDrag);

        // A touch that starts on a dot, and any touch with two fingers, belongs to the graph: keep the
        // browser from scrolling or zooming the page meanwhile. A single finger on the background is left alone.
        let ownTouch = false;
        svg.addEventListener('touchstart', (event) => {
            ownTouch = ownTouch || event.touches.length > 1 || Boolean(event.target.closest && event.target.closest('.node'));
        }, {passive: true});
        svg.addEventListener('touchmove', (event) => {
            if ((ownTouch || event.touches.length > 1) && event.cancelable) {
                event.preventDefault();
            }
        }, {passive: false});
        const touchDone = (event) => {
            if (!event.touches.length) {
                ownTouch = false;
            }
        };
        svg.addEventListener('touchend', touchDone);
        svg.addEventListener('touchcancel', touchDone);
        svg.addEventListener('pointerleave', () => {
            if (!drag) {
                hoverId = null;
                hideTip();
                highlight();
            }
        });

        // ----- hover, focus, selection -----

        function showTip(node) {
            const status = node.kind === 'topic' ? `<span class="${colorOf(node)}">${glyph(node.status, 13)}</span> ` : '';
            graphTip.innerHTML = node.kind === 'post' ? `${isUseCase(node) ? 'Use case' : 'Article'} · ${esc(short(node.name, 60))}` : `${status}${esc(node.name)}`;
            const c = node.el.getBoundingClientRect();
            const b = card.getBoundingClientRect();
            graphTip.style.left = `${c.left + c.width / 2 - b.left}px`;
            graphTip.style.top = `${c.top - b.top}px`;
            graphTip.classList.add('is-shown');
        }
        function hideTip() {
            graphTip.classList.remove('is-shown');
        }

        function hoverFrom(event) {
            const g = event.target.closest && event.target.closest('.node');
            const id = g ? g.dataset.id : null;
            if (id === hoverId) {
                return;
            }
            hoverId = id;
            highlight();
            id ? showTip(byId.get(id)) : hideTip();
        }

        nodesEl.addEventListener('focusin', (event) => {
            const g = event.target.closest('.node');
            if (g) {
                hoverId = g.dataset.id;
                highlight();
                showTip(byId.get(hoverId));
            }
        });
        nodesEl.addEventListener('focusout', () => {
            hoverId = null;
            hideTip();
            highlight();
        });
        nodesEl.addEventListener('keydown', (event) => {
            const g = event.target.closest('.node');
            if (g && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault();
                select(g.dataset.id);
            }
        });

        function matches(node) {
            if (node.kind === 'tile') {
                return true;
            }
            if (node.kind === 'post') {
                return filter === 'all' && match.test(`${node.name} ${node.post.take || ''}`.toLowerCase());
            }
            return (filter === 'all' || node.status === filter) && match.test(node.topic.search);
        }

        function highlight() {
            const focusId = hoverId || selectedId;
            const near = new Set(focusId && adjacency.has(focusId) ? [focusId].concat(adjacency.get(focusId).map(x => x.id)) : []);
            svg.classList.toggle('is-focus', near.size > 0);
            nodes.forEach((node) => {
                node.dim = !matches(node);
                node.el.classList.toggle('is-dim', node.dim && !near.has(node.id));
                node.el.classList.toggle('is-hit', match.active && !node.dim && node.kind !== 'tile');
                node.el.classList.toggle('is-near', near.has(node.id));
                node.el.classList.toggle('is-selected', node.id === selectedId);
            });
            edges.forEach((edge) => {
                const touches = Boolean(focusId) && (edge.a === focusId || edge.b === focusId);
                edge.el.classList.toggle('is-near', touches);
                edge.el.classList.toggle('is-dim', (edge.na.dim || edge.nb.dim) && !touches);
            });
        }

        const KIND = {link: 'Link', blog: 'Links here', structure: 'Tile'};
        const kindOf = (c, other) => (other.kind === 'post' ? (isUseCase(other) ? 'Use case' : 'Article') : KIND[c.kind]);
        const RANK = {link: 0, blog: 1, structure: 2};

        function select(id) {
            selectedId = id;
            highlight();
            if (!id) {
                panel.hidden = true;
                return;
            }
            const node = byId.get(id);
            const connected = adjacency.get(id).slice().sort((a, b) => RANK[a.kind] - RANK[b.kind]);
            let head;
            let action;
            if (node.kind === 'tile') {
                crumbEl.textContent = 'Tile';
                head = `<h2 class="brain-graph-title">${esc(node.name)}</h2><p class="brain-graph-take">${node.tile.topics.length} topic${node.tile.topics.length === 1 ? '' : 's'}.</p>`;
                action = `<button class="brain-graph-open" type="button" data-graph-show-tile="${esc(node.tile.slug)}">Show tile</button>`;
            } else if (node.kind === 'post') {
                const what = isUseCase(node) ? 'Use case' : 'Article';
                crumbEl.textContent = node.post.date ? `${what} · ${new Date(node.post.date).toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric'})}` : what;
                head = `<h2 class="brain-graph-title">${esc(node.name)}</h2>${node.post.take ? `<p class="brain-graph-take">${esc(node.post.take)}</p>` : ''}`;
                action = `<a class="brain-graph-open" href="${esc(node.url)}">${isUseCase(node) ? 'Open the use case' : 'Read the article'}</a>`;
            } else {
                crumbEl.textContent = node.tile.name;
                const take = $('.brain-topic-take', node.topic.el || document.createElement('i'));
                head = `<p class="brain-status-pill ${colorOf(node)}">${glyph(node.status, 13)}<span>${LABEL[node.status]}</span></p>` +
                    `<h2 class="brain-graph-title">${esc(node.name)}</h2>${take ? `<p class="brain-graph-take">${esc(take.textContent)}</p>` : ''}`;
                action = `<a class="brain-graph-open" href="${esc(node.url)}" data-brain-open>Open topic</a>`;
            }
            const list = connected.map((c) => {
                const other = byId.get(c.id);
                const mark = other.kind === 'topic' ? `<span class="${colorOf(other)}">${glyph(other.status, 14)}</span>` :
                    other.kind === 'post' ? `<span class="brain-graph-mark is-${postKind(other)}" aria-hidden="true"></span>` : `<span class="brain-graph-mark is-tile ${colorOf(other)}" aria-hidden="true"></span>`;
                return `<li><button type="button" data-graph-go="${esc(c.id)}">${mark}<span>${esc(other.name)}</span><span class="brain-graph-kind is-${c.kind}">${kindOf(c, other)}</span></button></li>`;
            }).join('');
            panelBody.innerHTML = `${head}${action}` +
                (connected.length ? `<h3 class="brain-graph-sub">Local graph</h3>${localGraph(node, connected)}` : '') +
                `<h3 class="brain-graph-sub">Connected (${connected.length})</h3>` +
                (list ? `<ul class="brain-graph-conn">${list}</ul>` : '<p class="brain-graph-none">No links yet.</p>');
            panel.hidden = false;
            centerOn(node);
        }

        /** The selected note in the middle, its neighbours around it (Obsidian's local graph). */
        function localGraph(node, connected) {
            const points = connected.map((c, i) => {
                const angle = -Math.PI / 2 + i / connected.length * Math.PI * 2;
                return {c, other: byId.get(c.id), x: Math.cos(angle) * 100, y: Math.sin(angle) * 58};
            });
            const fill = other => (other.kind === 'post' ? `is-${postKind(other)}` : colorOf(other));
            return '<svg class="brain-graph-local" viewBox="-150 -85 300 170" aria-hidden="true" focusable="false">' +
                points.map(p => `<line class="edge is-${p.c.kind}" x1="0" y1="0" x2="${p.x.toFixed(1)}" y2="${p.y.toFixed(1)}"/>`).join('') +
                points.map(p => `<g class="${fill(p.other)}" data-graph-go="${esc(p.c.id)}" transform="translate(${p.x.toFixed(1)},${p.y.toFixed(1)})"><circle class="g-fill" r="6"/><text text-anchor="middle" dy="17">${esc(short(p.other.name, 18))}</text></g>`).join('') +
                `<g class="${fill(node)}"><circle class="g-fill g-centre" r="10"/></g></svg>`;
        }

        panel.addEventListener('click', (event) => {
            const go = event.target.closest('[data-graph-go]');
            const tileBtn = event.target.closest('[data-graph-show-tile]');
            if (go) {
                select(go.dataset.graphGo);
                const target = byId.get(go.dataset.graphGo);
                if (target && target.el && go.tagName === 'BUTTON') {
                    target.el.focus({preventScroll: true});
                }
            } else if (tileBtn) {
                setView('tiles');
                location.hash = tileBtn.dataset.graphShowTile;
            }
        });
        $('[data-graph-close]', panel).addEventListener('click', () => {
            const last = selectedId && byId.get(selectedId);
            select(null);
            if (last && last.el) {
                last.el.focus({preventScroll: true});
            }
        });
        graphView.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && !panel.hidden) {
                event.stopPropagation();
                select(null);
            }
        });

        $$('[data-graph-opt]', graphView).forEach(input => input.addEventListener('change', () => {
            opts[input.dataset.graphOpt] = input.checked;
            build();
            run(0.6);
            setTimeout(() => fit(350), reduceMotion ? 0 : 900);
        }));

        let built = false;
        window.addEventListener('resize', () => {
            if (built && !graphView.hidden) {
                fit(0);
            }
        });

        return {
            /** Called whenever the graph becomes visible; lays it out the first time. */
            show() {
                if (!built) {
                    built = true;
                    build();
                    alpha = 1;
                    settle(400); // settle before the first paint, then let it breathe
                    fit(0);
                    run(0.15);
                }
            },
            /** The box's text (a matcher) or the status filter changed. */
            update(value) {
                match = value;
                if (built) {
                    highlight();
                }
            }
        };
    }

    const viewToggle = $('[data-view-toggle]');
    function setView(name) {
        view = name === 'graph' && graph ? 'graph' : 'tiles';
        if (viewToggle) {
            viewToggle.hidden = !graph;
            viewToggle.textContent = view === 'graph' ? 'Show tiles' : 'Enable graph view';
        }
        if (graphView) {
            graphView.hidden = view !== 'graph';
        }
        tilesEl.hidden = view !== 'tiles';
        toggleAll.hidden = view !== 'tiles';
        if (view === 'graph') {
            graph.show();
        }
        applyFilters();
    }

    if (viewToggle) {
        viewToggle.hidden = !graph;
        viewToggle.addEventListener('click', () => setView(view === 'graph' ? 'tiles' : 'graph'));
    }

    // ---------- filters + the find-or-ask box ----------

    let announceTimer;
    function applyFilters() {
        const match = matcher(search.value);
        const term = match.active;
        let anyTile = false;
        let topicHits = 0;
        let linkHits = 0;

        TILES.forEach((tile) => {
            let hits = 0;
            tile.topics.forEach((topic) => {
                const hit = match.test(topic.search);
                if (topic.el) {
                    topic.el.hidden = !hit;
                    topic.el.classList.toggle('is-dim', filter !== 'all' && topic.status !== filter);
                }
                hits += hit ? 1 : 0;
            });
            const links = term ? tile.links.filter(link => match.test(link.text)).length : 0;
            if (tile.countEl) {
                const total = Number(tile.countEl.dataset.total || 0);
                tile.countEl.textContent = term ? `${links} matching link${links === 1 ? '' : 's'}` : `${total} external link${total === 1 ? '' : 's'}`;
                tile.countEl.classList.toggle('is-hit', Boolean(term) && links > 0);
            }
            const visible = !term || hits + links > 0;
            tile.el.hidden = !visible;
            if (term && visible && tile.details) {
                tile.details.open = true;
            }
            anyTile = anyTile || visible;
            topicHits += hits;
            linkHits += links;
        });

        if (filter !== 'all' && !term) {
            details().forEach((d) => {
                d.open = true;
            });
        }

        if (graph) {
            graph.update(match);
        }

        empty.hidden = anyTile || view !== 'tiles';
        syncToggleAll();
        syncRows();

        const params = new URLSearchParams(location.search);
        term ? params.set('q', search.value.trim()) : params.delete('q');
        filter !== 'all' ? params.set('status', filter) : params.delete('status');
        view !== 'tiles' ? params.set('view', view) : params.delete('view');
        const query = params.toString();
        if (!reader || !reader.open) {
            history.replaceState(history.state, '', `${location.pathname}${query ? `?${query}` : ''}${location.hash}`);
            indexUrl = location.href;
        }

        clearTimeout(announceTimer);
        announceTimer = setTimeout(() => {
            if (term) {
                announce.textContent = `${topicHits} topic${topicHits === 1 ? '' : 's'} and ${linkHits} link${linkHits === 1 ? '' : 's'} match “${search.value.trim()}”.`;
            } else if (filter !== 'all') {
                announce.textContent = `Highlighting topics with status ${LABEL[filter]}.`;
            } else {
                announce.textContent = '';
            }
        }, 500);
    }

    // Status slider: the range input holds the value; the labels under it set it too
    const STOPS = ['all', ...ORDER];
    const range = $('[data-brain-range-input]');
    const stops = $$('[data-brain-range] [data-filter]');
    function setFilter(value) {
        filter = ORDER.includes(value) ? value : 'all';
        if (range) {
            range.value = String(STOPS.indexOf(filter));
            range.setAttribute('aria-valuetext', LABEL[filter] || 'All');
        }
        stops.forEach(stop => stop.classList.toggle('is-on', stop.dataset.filter === filter));
    }
    if (range) {
        range.addEventListener('input', () => {
            setFilter(STOPS[Number(range.value)]);
            applyFilters();
        });
    }
    stops.forEach(stop => stop.addEventListener('click', () => {
        setFilter(stop.dataset.filter);
        applyFilters();
        if (range) {
            range.focus({preventScroll: true});
        }
    }));
    // Keystrokes only filter; Enter submits the form, which asks (lib/ask.js, in main.min.js)
    search.addEventListener('input', applyFilters);

    const initial = new URLSearchParams(location.search);
    search.value = initial.get('q') || '';
    setFilter(initial.get('status'));
    if (initial.get('view')) {
        setView(initial.get('view')); // also applies the filters
    } else if (search.value || filter !== 'all') {
        applyFilters();
    }

    // "/" jumps to the find-or-ask box (lib/find.js leaves the key to this page when the box is here)
    document.addEventListener('keydown', (event) => {
        if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target) || document.querySelector('dialog[open]')) {
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        search.focus();
        search.select();
    }, true);

    // ---------- reader overlay ----------

    if (!reader || typeof reader.showModal !== 'function' || !window.fetch || !window.DOMParser) {
        return; // Links keep working as normal navigation
    }

    const readerBody = $('[data-reader-body]', reader);
    const readerScroll = $('[data-reader-scroll]', reader);
    const crumb = $('[data-reader-crumb]', reader);
    const prevBtn = $('[data-reader-prev]', reader);
    const nextBtn = $('[data-reader-next]', reader);
    const closeBtn = $('[data-reader-close]', reader);
    const pageLink = $('[data-reader-page]', reader);
    const readerAnnounce = $('[data-reader-announce]', reader);
    const indexTitle = document.title;
    const cache = new Map();
    let current = null;
    let lastTrigger = null;
    let pushed = false; // true while the reader owns the history entry it pushed

    const LINK_PAGES = TILES.filter(tile => tile.linksPage).map(tile => ({url: tile.linksPage.url, tile}));

    function load(url) {
        const target = new URL(url, location.href);
        if (target.origin !== location.origin) {
            return Promise.reject(new Error('Only same-origin pages open in the reader'));
        }
        const key = target.href;
        if (!cache.has(key)) {
            const request = fetch(key, {credentials: 'same-origin', headers: {Accept: 'text/html'}})
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}`);
                    }
                    return response.text().then((html) => {
                        const parsed = new DOMParser().parseFromString(html, 'text/html');
                        const doc = $('[data-brain-doc]', parsed);
                        if (!doc) {
                            throw new Error('Not a brain page');
                        }
                        // response.url is the final URL after Ghost's 301 to /brain/<name>/
                        return {url: response.url || key, title: parsed.title, doc};
                    });
                });
            request.catch(() => cache.delete(key));
            cache.set(key, request);
        }
        return cache.get(key);
    }

    function sequenceFor(doc) {
        if (doc.dataset.brainDoc === 'links') {
            const index = LINK_PAGES.findIndex(item => item.tile.slug === doc.dataset.tile);
            return {index, items: LINK_PAGES};
        }
        return {index: ALL_TOPICS.findIndex(topic => topic.slug === doc.dataset.slug), items: ALL_TOPICS};
    }

    function render(entry) {
        const doc = document.importNode(entry.doc, true);
        readerBody.replaceChildren(doc);
        enhanceDoc(doc, {filter: search.value.trim()});
        if (window.FlorezNotes) {
            window.FlorezNotes.enhance(doc);
        }
        readerScroll.scrollTop = 0;

        const title = doc.dataset.title || '';
        crumb.innerHTML = `${esc(doc.dataset.tileName)} / <b>${esc(title)}</b>`;
        pageLink.href = entry.url;
        document.title = entry.title || indexTitle;

        const {index, items} = sequenceFor(doc);
        const kind = doc.dataset.brainDoc === 'links' ? 'links page' : 'topic';
        const prev = index > 0 ? items[index - 1] : null;
        const next = index !== -1 && index < items.length - 1 ? items[index + 1] : null;
        prevBtn.disabled = !prev;
        nextBtn.disabled = !next;
        prevBtn.setAttribute('aria-label', prev ? `Previous ${kind}: ${prev.title || prev.tile.name}` : `No previous ${kind}`);
        nextBtn.setAttribute('aria-label', next ? `Next ${kind}: ${next.title || next.tile.name}` : `No next ${kind}`);
        prevBtn.onclick = () => prev && open(prev.url, null);
        nextBtn.onclick = () => next && open(next.url, null);
        readerAnnounce.textContent = `Showing ${title}${doc.dataset.tileName ? ` in ${doc.dataset.tileName}` : ''}.`;
        current = entry;
    }

    let openCount = 0;

    /**
     * @param {string} url
     * @param {Element|null} trigger element to return focus to on close
     * @param {'none'} [mode] 'none' leaves the address bar alone (Back/Forward already moved it).
     *   Otherwise the reader pushes one history entry when it opens and replaces it while it
     *   stays open, so Close is always a single step back.
     */
    function open(url, trigger, mode) {
        const request = ++openCount;
        if (!reader.open) {
            lastTrigger = trigger || document.activeElement;
        }
        readerBody.classList.add('is-loading');
        return load(url).then((entry) => {
            if (request !== openCount) {
                return; // a later click won; ignore this response
            }
            readerBody.classList.remove('is-loading');
            render(entry);
            if (mode === 'none') {
                pushed = true; // Forward onto a reader entry: the index entry sits right below it
            } else if (reader.open) {
                history.replaceState({brainReader: entry.url}, '', entry.url);
            } else {
                history.pushState({brainReader: entry.url}, '', entry.url);
                pushed = true;
            }
            if (!reader.open) {
                reader.showModal();
                document.body.classList.add('brain-reader-open');
                closeBtn.focus();
            } else if (document.activeElement && document.activeElement.disabled) {
                closeBtn.focus();
            }
        }).catch(() => {
            if (request === openCount) {
                location.assign(url); // fall back to a normal page load
            }
        });
    }

    function cleanup() {
        current = null;
        pushed = false;
        document.body.classList.remove('brain-reader-open');
        document.title = indexTitle;
        if (lastTrigger && document.contains(lastTrigger) && !lastTrigger.closest('[hidden]')) {
            lastTrigger.focus();
        }
    }

    function hide() {
        current = null;
        if (reader.open) {
            reader.close();
        }
        cleanup();
    }

    function close() {
        if (pushed && history.state && history.state.brainReader) {
            pushed = false;
            history.back(); // popstate hides the reader
        } else {
            history.replaceState(null, '', indexUrl);
            hide();
        }
    }

    window.addEventListener('popstate', (event) => {
        const url = event.state && event.state.brainReader;
        if (url) {
            open(url, null, 'none');
        } else if (reader.open) {
            hide();
        }
    });

    reader.addEventListener('cancel', (event) => {
        event.preventDefault(); // Esc: close through history so Back stays in sync
        close();
    });
    // The browser can close the dialog itself (e.g. Esc pressed twice without other input)
    reader.addEventListener('close', () => {
        if (!current) {
            return;
        }
        if (pushed && history.state && history.state.brainReader) {
            history.back();
        } else {
            history.replaceState(null, '', indexUrl);
        }
        cleanup();
    });
    reader.addEventListener('click', (event) => {
        if (event.target === reader) {
            close(); // click on the backdrop
        }
    });
    closeBtn.addEventListener('click', close);

    reader.addEventListener('keydown', (event) => {
        // Arrow keys in a code box scroll it sideways instead of switching topics
        if (isTyping(event.target) || event.target.closest('pre') || event.metaKey || event.ctrlKey || event.altKey) {
            return;
        }
        if (event.key === 'ArrowLeft' && !prevBtn.disabled) {
            prevBtn.click();
        } else if (event.key === 'ArrowRight' && !nextBtn.disabled) {
            nextBtn.click();
        }
    });

    // Open topic and links pages in the reader; modified clicks keep their normal behaviour
    document.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-brain-open]');
        if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            return;
        }
        event.preventDefault();
        const inReader = reader.open && reader.contains(link);
        open(link.href, inReader ? null : link);
    });

    // Warm the cache when a pointer rests on a link
    document.addEventListener('pointerover', (event) => {
        const link = event.target.closest && event.target.closest('a[data-brain-open]');
        if (link && link.href) {
            load(link.href).catch(() => {});
        }
    });
})();
